import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MAX_TERMINAL_LINES = 14;

const Hero = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [terminalLines, setTerminalLines] = useState([]);
  const [input, setInput] = useState('');
  const [displayRole, setDisplayRole] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);
  const terminalEndRef = useRef(null);
  const bootRanRef = useRef(false);

  const roles = ['Software Engineer', 'Full Stack Developer', 'Security Researcher', 'ML Enthusiast', 'Network Enthusiast', 'Database Manager', 'Systems Engineer'];

  const bootSequence = [
    'Initializing portfolio v2.0...',
    'Loading modules... [OK]',
    'Establishing connection... [OK]',
    'Welcome to Divita\'s Developer Portfolio',
    '',
    'Type "help" for available commands',
    ''
  ];

  // ✅ rolling buffer helper (terminal only)
  const pushLines = (lines) => {
    setTerminalLines(prev => {
      const updated = [...prev, ...lines];
      return updated.slice(-MAX_TERMINAL_LINES);
    });
  };

  // useEffect(() => {
  //   bootSequence.forEach((line, index) => {
  //     setTimeout(() => {
  //       pushLines([line]);
  //     }, index * 300);
  //   });
  // }, []);

  useEffect(() => {
    if (bootRanRef.current) return;
    bootRanRef.current = true;

    bootSequence.forEach((line, index) => {
      setTimeout(() => {
        pushLines([line]);
      }, index * 300);
    });

  }, []);

  // ✅ terminal auto-scroll (fixed target)
  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [terminalLines]);

  // Typing effect for roles (unchanged)
  useEffect(() => {
    let currentIndex = 0;
    const currentRole = roles[roleIndex];
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= currentRole.length) {
        setDisplayRole(currentRole.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setRoleIndex((prev) => (prev + 1) % roles.length);
        }, 2000);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, [roleIndex]);

  const commands = {
    help: 'Available commands: about, skills, projects, experience, internships, education, contact, clear, whoami, ls',
    about: 'Software Developer & Technology Enthusiast | MS CS @ CU Boulder',
    skills: 'Java, Python, C/C++, Network Systems, Distributed Systems, Database Systems, Cloud, Security, ML/NLP, Blockchain',
    projects: '15+ projects including personal, academic, and publications. See Projects section for details.',
    experience: '2+ years exp: TIAA (Software Engineer) -> CU Boulder (Grad Student)',
    internships: 'Internships at Ascend Cargo Systems, Richedu, JMES',
    education: 'MS CS @ CU Boulder | B.E CS @ Mumbai University',
    contact: 'Email: divita.phadakale@colorado.edu | GitHub: github.com/DivitaP',
    whoami: 'Divita Phadakale | grad-student | code-enthusiast',
    ls: 'about.md  skills.json  projects/  experience.log  education.txt  resume.pdf',
  };

  // ✅ terminal handler (fixed)
  const handleCommand = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const command = input.trim().toLowerCase();

      if (command === 'clear') {
        setTerminalLines([]);
        setInput('');
        return;
      }

      if (!command) return;

      if (command in commands) {
        pushLines([`$ ${input}`, commands[command]]);
      } else {
        pushLines([
          `$ ${input}`,
          `command not found: ${command}. Type "help" for available commands.`,
        ]);
      }

      setInput('');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  return (
    <section className="hero" id="home">
      <div className="hero-particles">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="particle"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              y: [null, Math.random() * window.innerHeight],
              x: [null, Math.random() * window.innerWidth],
            }}
            transition={{
              duration: Math.random() * 20 + 10,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
      </div>

      <div className="container">
        <div className="hero-content" ref={ref}>

          {/* LEFT SIDE — UNCHANGED */}
          <motion.div
            className="hero-text"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.div className="status-badge" variants={itemVariants}>
              <span className="status-dot"></span>
              <span>Online</span>
            </motion.div>

            <motion.div className="hero-title-wrapper" variants={itemVariants}>
              <h1 className="hero-title">
                <motion.span className="comment">// Hello World</motion.span>
                <motion.span>
                  I'm <span className="highlight glitch">Divita Phadakale</span>
                </motion.span>
                <motion.span className="subtitle">
                  <span className="keyword">const</span> role ={' '}
                  <span className="string">"{displayRole}<span className="cursor-blink">|</span>"</span>;
                </motion.span>
              </h1>
            </motion.div>
          </motion.div>

          {/* TERMINAL — sleek + rectangular */}
<motion.div
  className="hero-terminal"
  initial={{ opacity: 0, x: 50, rotateY: -15 }}
  animate={inView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
  transition={{ duration: 0.8, delay: 0.3 }}
  whileHover={{ scale: 1.02 }}
>
  <div
    className="terminal-window"
    style={{
      borderRadius: "6px",
      overflow: "hidden",
      border: "1px solid #1f1f1f",
      background: "#0b0b0b",
      boxShadow: "0 20px 60px rgba(0,0,0,.6)"
    }}
  >
    <div
      className="terminal-header"
      style={{
        padding: "10px 14px",
        borderBottom: "1px solid #1a1a1a",
        background: "#111"
      }}
    >
      <div className="terminal-buttons">
        <span className="btn-close"></span>
        <span className="btn-minimize"></span>
        <span className="btn-maximize"></span>
      </div>
      <div className="terminal-title">divita@portfolio:~$</div>
    </div>

    {/* scroll area */}
    <div
      className="terminal-body"
      style={{
        height: "320px",
        width: "680px",
        overflowY: "auto",
        padding: "14px 16px",
        fontFamily: "monospace",
        fontSize: "0.9rem",
        lineHeight: "1.5"
      }}
    >
      {terminalLines.map((line, index) => (
        <motion.div
          key={index}
          className="terminal-line"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
        >
          {line}
        </motion.div>
      ))}
      <div ref={terminalEndRef} />
    </div>

    {/* fixed input bar */}
    <div
      className="terminal-input-wrapper"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 16px",
        borderTop: "1px solid #1a1a1a",
        background: "#0e0e0e"
      }}
    >
      <span
        className="prompt"
        style={{ color: "#00ff41", fontFamily: "monospace" }}
      >
        $
      </span>

      <input
        type="text"
        className="terminal-input"
        placeholder="Type 'help' for commands..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleCommand}
        autoComplete="off"
        spellCheck="false"
        style={{
          flex: 1,
          background: "transparent",
          border: "none",
          outline: "none",
          color: "#e6e6e6",
          fontFamily: "monospace",
          fontSize: "0.9rem"
        }}
      />
    </div>
  </div>

  <motion.div className="terminal-hint">
    Try typing: <code>help</code>, <code>experience</code>, <code>ls</code>, <code>clear</code>
  </motion.div>
</motion.div>


        </div>
      </div>
    </section>
  );
};

export default Hero;
